import { useState, useEffect, useCallback, useRef } from "react";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import { LogOut, BarChart3, List, Trash2, Download, ChevronDown, ChevronUp, Copy, Globe, MapPin, Link2, AlertCircle, Users, Mail, Send, Megaphone, Eye, MousePointer, CheckCircle2, ArrowLeft, Search, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const API = process.env.REACT_APP_SURVEY_API_URL || "https://eamico-survey-api.eamico-inc.workers.dev";
const C = ["#1a73e8","#34a853","#ea4335","#fbbc04","#9334e6","#ff6d01","#46bdc6","#7baaf7","#f07b72","#fcd04f"];

/* ── Skeleton ── */
const Skel = ({ w = "100%", h = 20, r = 8 }) => (
  <div className="animate-pulse bg-[#e8eaed] rounded" style={{ width: w, height: h, borderRadius: r }} />
);
const SkeletonCards = ({ n = 4 }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="bg-white rounded-xl border border-[#e8eaed] p-5 space-y-3">
        <Skel w={80} h={10} /><Skel w={60} h={28} /><Skel w={50} h={10} />
      </div>
    ))}
  </div>
);

/* ── Auth ── */
const AuthScreen = ({ onAuth }) => {
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");
  const [registered, setRegistered] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/auth/registered`).then(r => r.json())
      .then(d => { setRegistered(d.registered); setStatus("ready"); })
      .catch(() => { setError("Cannot reach API."); setStatus("error"); });
  }, []);

  const handleRegister = async () => {
    setError(""); setStatus("registering");
    try {
      const { options, challengeId } = await (await fetch(`${API}/api/auth/register-options`, { method: "POST" })).json();
      const credential = await startRegistration({ optionsJSON: options });
      const { verified } = await (await fetch(`${API}/api/auth/register-verify`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challengeId, response: credential }) })).json();
      if (!verified) throw new Error("Not verified");
      setRegistered(true); setStatus("ready");
    } catch (e) { setError(e.message); setStatus("ready"); }
  };

  const handleLogin = async () => {
    setError(""); setStatus("authenticating");
    try {
      const { options, challengeId } = await (await fetch(`${API}/api/auth/login-options`, { method: "POST" })).json();
      const credential = await startAuthentication({ optionsJSON: options });
      const { verified, token } = await (await fetch(`${API}/api/auth/login-verify`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challengeId, response: credential }) })).json();
      if (!verified) throw new Error("Auth failed");
      localStorage.setItem("survey_admin_token", token); onAuth(token);
    } catch (e) { setError(e.message); setStatus("ready"); }
  };

  const waiting = status === "registering" || status === "authenticating";
  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center px-4" data-testid="survey-admin-auth">
      <div className="bg-white rounded-2xl shadow-sm border border-[#e8eaed] p-10 max-w-sm w-full text-center">
        <img src="https://customer-assets.emergentagent.com/job_eamico-core/artifacts/uowh0b6l_Untitled%20design.png" alt="Eamico" className="h-7 mx-auto mb-6" />
        <h1 className="text-[20px] font-normal text-[#202124] mb-1">Admin</h1>
        <p className="text-[13px] text-[#5f6368] mb-8">
          {status === "checking" && "Checking..."}
          {status === "ready" && !registered && "Register your security key."}
          {status === "ready" && registered && "Tap your security key to continue."}
          {waiting && "Waiting for key..."}
          {status === "error" && "Connection error."}
        </p>
        {status === "ready" && !registered && <button onClick={handleRegister} className="w-full py-3 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-[14px] font-medium rounded-lg transition-colors" data-testid="register-key-btn">Register Key</button>}
        {status === "ready" && registered && <button onClick={handleLogin} className="w-full py-3 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-[14px] font-medium rounded-lg transition-colors" data-testid="login-key-btn">Authenticate</button>}
        {waiting && <div className="w-6 h-6 border-2 border-[#1a73e8] border-t-transparent rounded-full animate-spin mx-auto" />}
        {error && <p className="mt-4 text-[12px] text-red-500" data-testid="auth-error">{error}</p>}
      </div>
    </div>
  );
};

/* ── Stat Card ── */
const Stat = ({ label, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-[#e8eaed] p-4 relative overflow-hidden group hover:shadow-sm transition-shadow">
    {color && <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: color }} />}
    <p className="text-[11px] font-medium text-[#80868b] uppercase tracking-wide">{label}</p>
    <p className="text-[26px] font-light text-[#202124] mt-0.5 tracking-tight">{value}</p>
    {sub && <p className="text-[11px] text-[#80868b] mt-0.5">{sub}</p>}
  </div>
);

/* ── Charts ── */
const toData = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name: name.length > 24 ? name.slice(0, 22) + "..." : name, value }));

const HBar = ({ data, title, color = "#1a73e8" }) => {
  const d = toData(data);
  if (!d.length) return null;
  return (
    <div className="bg-white rounded-xl border border-[#e8eaed] p-5">
      <h3 className="text-[13px] font-medium text-[#202124] mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={Math.max(d.length * 32, 64)}>
        <BarChart data={d} layout="vertical" margin={{ left: 0, right: 12, top: 0, bottom: 0 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "#5f6368" }} />
          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e8eaed", boxShadow: "0 2px 8px rgba(0,0,0,.08)" }} />
          <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} barSize={16} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const Donut = ({ data, title }) => {
  const d = toData(data);
  if (!d.length) return null;
  return (
    <div className="bg-white rounded-xl border border-[#e8eaed] p-5">
      <h3 className="text-[13px] font-medium text-[#202124] mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={d} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 10 }}>
            {d.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e8eaed" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Table ── */
const Th = ({ children, ...p }) => <th className="pb-2 pr-3 font-medium text-[11px] text-[#80868b] uppercase tracking-wider" {...p}>{children}</th>;
const Td = ({ children, mono, muted, ...p }) => <td className={`py-2.5 pr-3 text-[13px] ${mono ? "font-mono text-[12px]" : ""} ${muted ? "text-[#80868b]" : "text-[#202124]"}`} {...p}>{children}</td>;

/* ── Dashboard ── */
const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("stats");
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);
  const [partials, setPartials] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [counts, setCounts] = useState({ responses: 0, partials: 0, waitlist: 0 });
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [emailDraft, setEmailDraft] = useState({ subject: "", html: "" });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [tabLoading, setTabLoading] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const loaded = useRef({});
  const h = { Authorization: `Bearer ${token}` };

  const fetchTab = useCallback(async (id, force = false) => {
    if (loaded.current[id] && !force) return;
    setTabLoading(p => ({ ...p, [id]: true }));
    try {
      // Always get counts first time
      if (!loaded.current._c) {
        const cRes = await fetch(`${API}/api/admin/counts`, { headers: { Authorization: `Bearer ${token}` } });
        if (cRes.ok) setCounts(await cRes.json());
        loaded.current._c = true;
      }
      const hdr = { Authorization: `Bearer ${token}` };
      if (id === "stats" || id === "geo") {
        const r = await fetch(`${API}/api/survey/stats`, { headers: hdr });
        if (r.status === 401) { onLogout(); return; }
        setStats(await r.json());
        loaded.current.stats = loaded.current.geo = true;
      } else if (id === "responses") {
        const r = await fetch(`${API}/api/survey/responses`, { headers: hdr });
        if (r.status === 401) { onLogout(); return; }
        setResponses((await r.json()).responses || []);
        loaded.current.responses = true;
      } else if (id === "incomplete") {
        const r = await fetch(`${API}/api/survey/partials`, { headers: hdr });
        if (r.status === 401) { onLogout(); return; }
        setPartials((await r.json()).partials || []);
        loaded.current.incomplete = true;
      } else if (id === "waitlist") {
        const r = await fetch(`${API}/api/waitlist`, { headers: hdr });
        if (r.status === 401) { onLogout(); return; }
        setWaitlist((await r.json()).entries || []);
        loaded.current.waitlist = true;
      } else if (id === "newsletter") {
        const [wR, nR] = await Promise.all([
          loaded.current.waitlist && !force ? null : fetch(`${API}/api/waitlist`, { headers: hdr }),
          fetch(`${API}/api/newsletter`, { headers: hdr }),
        ]);
        if (wR) { setWaitlist((await wR.json()).entries || []); loaded.current.waitlist = true; }
        if (nR.status === 401) { onLogout(); return; }
        setNewsletter((await nR.json()).subscribers || []);
        loaded.current.newsletter = true;
      } else if (id === "campaigns") {
        const r = await fetch(`${API}/api/email/stats`, { headers: hdr });
        if (r.status === 401) { onLogout(); return; }
        setCampaigns((await r.json()).campaigns || []);
        loaded.current.campaigns = true;
      }
    } catch (e) { console.error(e); }
    setTabLoading(p => ({ ...p, [id]: false }));
  }, [token, onLogout]);

  useEffect(() => { fetchTab(tab); }, [tab, fetchTab]);

  const refresh = async () => { setRefreshing(true); loaded.current[tab] = false; await fetchTab(tab, true); setRefreshing(false); };
  const isLoading = tabLoading[tab];

  const del = async (id) => { if (!window.confirm("Delete?")) return; await fetch(`${API}/api/survey/response/${id}`, { method: "DELETE", headers: h }); loaded.current.responses = loaded.current.stats = false; fetchTab(tab, true); };
  const delPartial = async (id) => { if (!window.confirm("Delete?")) return; await fetch(`${API}/api/survey/partial/${id}`, { method: "DELETE", headers: h }); loaded.current.incomplete = false; fetchTab("incomplete", true); };

  const sendTest = async () => {
    if (!emailDraft.subject || !emailDraft.html) return;
    setSending(true); setSendResult(null);
    try {
      const r = await fetch(`${API}/api/email/send`, { method: "POST", headers: { ...h, "Content-Type": "application/json" }, body: JSON.stringify({ ...emailDraft, testMode: true }) });
      setSendResult(await r.json());
    } catch { setSendResult({ error: "Failed" }); }
    setSending(false);
  };

  const loadCampaign = async (cid) => {
    setSelectedCampaign(cid); setCampaignDetail(null);
    try { const r = await fetch(`${API}/api/email/stats?campaign=${cid}`, { headers: h }); if (r.ok) setCampaignDetail(await r.json()); } catch {}
  };

  const deleteCampaign = async (cid) => {
    if (!window.confirm("Delete this campaign?")) return;
    await fetch(`${API}/api/email/campaign/${cid}`, { method: "DELETE", headers: h });
    setCampaigns(prev => prev.filter(c => c.id !== cid));
    if (selectedCampaign === cid) { setSelectedCampaign(null); setCampaignDetail(null); }
  };

  const exportCSV = () => {
    if (!responses.length) return;
    const keys = [...new Set(responses.flatMap(r => { const k = Object.keys(r).filter(x => x !== "geo"); if (r.geo) k.push("geo_ip","geo_country","geo_city","geo_region","geo_timezone","geo_isp"); return k; }))];
    const rows = [keys.join(","), ...responses.map(r => { const f = { ...r }; if (r.geo) { f.geo_ip=r.geo.ip; f.geo_country=r.geo.country; f.geo_city=r.geo.city; f.geo_region=r.geo.region; f.geo_timezone=r.geo.timezone; f.geo_isp=r.geo.asOrganization; } delete f.geo; return keys.map(k => `"${String(f[k]??"").replace(/"/g,'""')}"`).join(","); })];
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" })); a.download = `eamico-survey-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const logout = async () => { await fetch(`${API}/api/auth/logout`, { method: "POST", headers: h }); localStorage.removeItem("survey_admin_token"); onLogout(); };

  const [copied, setCopied] = useState(false);
  const copyLink = () => { navigator.clipboard.writeText("https://eamico.com/survey"); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  // Filter helper
  const q = search.toLowerCase();
  const filteredResponses = responses.filter(r => !q || `${r.firstName} ${r.lastName} ${r.email} ${r.companyName}`.toLowerCase().includes(q));
  const filteredWaitlist = waitlist.filter(w => !q || `${w.firstName} ${w.lastName} ${w.email}`.toLowerCase().includes(q));

  const NAV = [
    { section: "Analytics", items: [{ id: "stats", icon: BarChart3, label: "Overview" }, { id: "geo", icon: Globe, label: "Geography" }] },
    { section: "Data", items: [{ id: "responses", icon: List, label: "Responses", count: responses.length || counts.responses }, { id: "incomplete", icon: AlertCircle, label: "Incomplete", count: partials.length || counts.partials }] },
    { section: "Audience", items: [{ id: "waitlist", icon: Users, label: "Waitlist", count: waitlist.length || counts.waitlist }, { id: "newsletter", icon: Mail, label: "Newsletter" }] },
    { section: "Email", items: [{ id: "campaigns", icon: Megaphone, label: "Campaigns" }] },
  ];

  const TITLES = { stats: "Overview", geo: "Geography", responses: "Responses", incomplete: "Incomplete", waitlist: "Waitlist", newsletter: "Newsletter", campaigns: selectedCampaign ? "Campaign Detail" : "Campaigns" };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex" data-testid="survey-admin-dashboard">
      {/* ── Sidebar ── */}
      <aside className="w-[240px] bg-white border-r border-[#e8eaed] flex flex-col fixed top-0 left-0 h-screen z-10">
        <div className="px-5 py-4 border-b border-[#e8eaed] flex items-center gap-2">
          <img src="https://customer-assets.emergentagent.com/job_eamico-core/artifacts/uowh0b6l_Untitled%20design.png" alt="Eamico" className="h-5" />
          <span className="text-[14px] font-medium text-[#202124]">Admin</span>
        </div>

        <nav className="flex-1 px-2.5 py-3 overflow-y-auto">
          {NAV.map(g => (
            <div key={g.section} className="mb-1">
              <p className="px-2.5 pt-3 pb-1.5 text-[10px] font-semibold text-[#9aa0a6] uppercase tracking-widest">{g.section}</p>
              {g.items.map(t => (
                <button key={t.id} onClick={() => { setTab(t.id); if (t.id === "campaigns") setSelectedCampaign(null); setSearch(""); }} data-testid={`tab-${t.id}`}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-all duration-150 ${tab === t.id ? "bg-[#e8f0fe] text-[#1a73e8] font-medium" : "text-[#3c4043] hover:bg-[#f1f3f4]"}`}>
                  <t.icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left truncate">{t.label}</span>
                  {t.count !== undefined && <span className={`text-[10px] min-w-[20px] text-center px-1.5 py-0.5 rounded-full font-medium ${tab === t.id ? "bg-[#1a73e8] text-white" : "bg-[#f1f3f4] text-[#5f6368]"}`}>{t.count}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* Survey link */}
        <div className="px-3 py-2 border-t border-[#e8eaed]">
          <button onClick={copyLink} className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12px] text-[#1a73e8] hover:bg-[#e8f0fe] transition-colors" data-testid="copy-link-btn">
            <Link2 className="w-3.5 h-3.5" />
            <span className="truncate flex-1 text-left">{copied ? "Copied!" : "eamico.com/survey"}</span>
            <Copy className="w-3 h-3" />
          </button>
        </div>

        <div className="px-2.5 py-2.5 border-t border-[#e8eaed] flex gap-1">
          <button onClick={exportCSV} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] text-[#5f6368] hover:bg-[#f1f3f4] transition-colors" data-testid="export-csv-btn"><Download className="w-3.5 h-3.5" />Export</button>
          <button onClick={logout} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] text-[#5f6368] hover:bg-[#f1f3f4] transition-colors" data-testid="logout-btn"><LogOut className="w-3.5 h-3.5" />Logout</button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="flex-1 ml-[240px] min-h-screen">
        {/* Top bar */}
        <div className="bg-white border-b border-[#e8eaed] sticky top-0 z-[5]">
          <div className="px-6 py-3 flex items-center gap-4">
            <h1 className="text-[16px] font-medium text-[#202124] shrink-0">{TITLES[tab] || ""}</h1>
            {/* Search */}
            {["responses", "waitlist", "incomplete"].includes(tab) && (
              <div className="flex-1 max-w-xs relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9aa0a6]" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-1.5 bg-[#f1f3f4] rounded-lg text-[13px] focus:outline-none focus:bg-[#e8eaed] transition-colors" />
              </div>
            )}
            <div className="flex-1" />
            <button onClick={refresh} className={`p-2 rounded-lg text-[#5f6368] hover:bg-[#f1f3f4] transition-all ${refreshing ? "animate-spin" : ""}`} title="Refresh"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="p-6">

        {/* ═══ OVERVIEW ═══ */}
        {tab === "stats" && (
          isLoading && !stats ? <div className="space-y-6"><SkeletonCards /><SkeletonCards /></div> : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Responses" value={stats?.total || 0} color="#1a73e8" />
              <Stat label="IDE Satisfaction" value={stats?.avgIdeSatisfaction || "—"} sub="/5" color="#34a853" />
              <Stat label="Hosting Satisfaction" value={stats?.avgHostingSatisfaction || "—"} sub="/5" color="#fbbc04" />
              <Stat label="Deploy Satisfaction" value={stats?.avgDeploymentSatisfaction || "—"} sub="/5" color="#ea4335" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Waitlist" value={stats?.waitlistYes || 0} /><Stat label="Newsletter" value={stats?.newsletterYes || 0} /><Stat label="Follow-up" value={stats?.followUpYes || 0} /><Stat label="Investors" value={stats?.investorYes || 0} />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <Donut data={stats?.sources} title="Traffic Sources" />
              <HBar data={stats?.sources} title="Source Breakdown" color="#ff6d01" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <Donut data={stats?.companyTypes} title="Company Types" />
              <Donut data={stats?.teamSizes} title="Team Sizes" />
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              <HBar data={stats?.ides} title="IDEs" color="#ea4335" />
              <HBar data={stats?.hostingProviders} title="Hosting" color="#fbbc04" />
              <HBar data={stats?.languages} title="Languages" color="#4285f4" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <HBar data={stats?.ideFrustrations} title="IDE Frustrations" color="#ea4335" />
              <HBar data={stats?.hostingFrustrations} title="Hosting Frustrations" color="#ea4335" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <HBar data={stats?.switchingBlockers} title="Switching Blockers" color="#9334e6" />
              <HBar data={stats?.roles} title="Roles" color="#1a73e8" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <Donut data={stats?.studioInterest} title="IDE Interest" />
              <Donut data={stats?.cloudInterest} title="Compute Interest" />
            </div>
          </div>)
        )}

        {/* ═══ GEO ═══ */}
        {tab === "geo" && (
          isLoading && !stats ? <SkeletonCards n={3} /> : (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Countries" value={Object.keys(stats?.countries || {}).length} color="#1a73e8" />
              <Stat label="Cities" value={Object.keys(stats?.cities || {}).length} color="#34a853" />
              <Stat label="Responses" value={stats?.total || 0} color="#fbbc04" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <HBar data={stats?.countries} title="By Country" color="#1a73e8" />
              <HBar data={stats?.cities} title="By City" color="#34a853" />
            </div>
          </div>)
        )}

        {/* ═══ RESPONSES ═══ */}
        {tab === "responses" && (
          isLoading && !responses.length ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="bg-white rounded-xl border border-[#e8eaed] p-5"><Skel h={16} w="40%" /><Skel h={12} w="60%" /><Skel h={12} w="30%" /></div>)}</div> : (
          <div className="space-y-2">
            <p className="text-[12px] text-[#80868b] mb-2">{filteredResponses.length} of {responses.length} response{responses.length !== 1 ? "s" : ""}</p>
            {filteredResponses.map(r => <ResponseRow key={r.id} r={r} onDel={del} />)}
            {filteredResponses.length === 0 && <Empty text="No responses found" />}
          </div>)
        )}

        {/* ═══ INCOMPLETE ═══ */}
        {tab === "incomplete" && (
          isLoading && !partials.length ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="bg-white rounded-xl border border-[#e8eaed] p-5"><Skel h={16} w="40%" /><Skel h={12} w="50%" /></div>)}</div> : (
          <div className="space-y-2">
            <p className="text-[12px] text-[#80868b] mb-2">{partials.length} incomplete</p>
            {partials.map(p => <PartialRow key={p.id} p={p} onDel={delPartial} />)}
            {partials.length === 0 && <Empty text="No incomplete submissions" />}
          </div>)
        )}

        {/* ═══ WAITLIST ═══ */}
        {tab === "waitlist" && (
          isLoading && !waitlist.length ? <SkeletonCards n={3} /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Total" value={waitlist.length} color="#1a73e8" />
              <Stat label="Ambassadors" value={[...new Set(waitlist.map(w => w.ambassador).filter(Boolean))].length} color="#34a853" />
              <Stat label="Earliest" value={waitlist.length ? new Date(waitlist[waitlist.length-1]?.timestamp||0).toLocaleDateString() : "—"} />
            </div>
            <div className="bg-white rounded-xl border border-[#e8eaed] overflow-hidden">
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full text-[13px]">
                  <thead className="sticky top-0 bg-[#f8f9fa]"><tr className="text-left border-b border-[#e8eaed]">
                    <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Ambassador</Th><Th>Date</Th>
                  </tr></thead>
                  <tbody>
                    {filteredWaitlist.map((w, i) => (
                      <tr key={i} className="border-b border-[#f1f3f4] hover:bg-[#f8f9fa] transition-colors">
                        <Td>{w.firstName} {w.lastName}</Td><Td muted>{w.email}</Td><Td muted>{w.phone || "—"}</Td><Td muted>{w.ambassador || "—"}</Td><Td muted>{w.timestamp ? new Date(w.timestamp).toLocaleDateString() : "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>)
        )}

        {/* ═══ NEWSLETTER ═══ */}
        {tab === "newsletter" && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Subscribers" value={newsletter.length} color="#1a73e8" />
              <Stat label="Waitlist" value={waitlist.length} color="#34a853" />
              <Stat label="Total Reach" value={waitlist.length + newsletter.length} color="#fbbc04" />
            </div>

            <div className="bg-white rounded-xl border border-[#e8eaed] p-5">
              <div className="flex items-center gap-2 mb-4">
                <Send className="w-4 h-4 text-[#1a73e8]" /><h3 className="text-[13px] font-medium text-[#202124]">Compose</h3>
                <span className="text-[10px] bg-[#fef7e0] text-[#b06000] px-2 py-0.5 rounded-full font-medium ml-auto">Test mode</span>
              </div>
              <input value={emailDraft.subject} onChange={e => setEmailDraft(d => ({ ...d, subject: e.target.value }))} placeholder="Subject..." className="w-full px-3 py-2 border border-[#dadce0] rounded-lg text-[13px] mb-2 focus:outline-none focus:border-[#1a73e8]" />
              <textarea value={emailDraft.html} onChange={e => setEmailDraft(d => ({ ...d, html: e.target.value }))} placeholder="HTML body..." rows={6} className="w-full px-3 py-2 border border-[#dadce0] rounded-lg text-[12px] font-mono mb-3 focus:outline-none focus:border-[#1a73e8] resize-none" />
              <div className="flex items-center gap-3">
                <button onClick={sendTest} disabled={sending || !emailDraft.subject || !emailDraft.html} className="px-4 py-2 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-[12px] font-medium rounded-lg transition-colors disabled:opacity-40"><Send className="w-3 h-3 inline mr-1.5" />{sending ? "Sending..." : "Send Test"}</button>
                <span className="text-[11px] text-[#80868b]">To Cameron.bergsma@eamico.com</span>
              </div>
              {sendResult && <p className={`mt-2 text-[12px] ${sendResult.error ? "text-red-500" : "text-[#34a853]"}`}>{sendResult.error || `Sent! ${sendResult.sent} delivered.`}</p>}
            </div>

            {newsletter.length > 0 && (
              <div className="bg-white rounded-xl border border-[#e8eaed] overflow-hidden">
                <div className="max-h-[40vh] overflow-y-auto">
                  <table className="w-full text-[13px]">
                    <thead className="sticky top-0 bg-[#f8f9fa]"><tr className="text-left border-b border-[#e8eaed]"><Th>Name</Th><Th>Email</Th><Th>Source</Th><Th>Date</Th></tr></thead>
                    <tbody>
                      {newsletter.map((s, i) => (
                        <tr key={i} className="border-b border-[#f1f3f4] hover:bg-[#f8f9fa]">
                          <Td>{s.firstName} {s.lastName}</Td><Td muted>{s.email}</Td><Td muted>{s.source || "—"}</Td><Td muted>{s.subscribedAt ? new Date(s.subscribedAt).toLocaleDateString() : "—"}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ CAMPAIGNS ═══ */}
        {tab === "campaigns" && (
          selectedCampaign && campaignDetail ? (
            <div className="space-y-5">
              <button onClick={() => { setSelectedCampaign(null); setCampaignDetail(null); }} className="flex items-center gap-1 text-[12px] font-medium text-[#1a73e8] hover:underline" data-testid="campaign-back-btn"><ArrowLeft className="w-3.5 h-3.5" />Back</button>
              <div className="flex items-center justify-between">
                <div><p className="text-[18px] font-normal text-[#202124]">{campaignDetail.campaignName || "Untitled"}</p><p className="text-[12px] text-[#80868b]">{campaignDetail.sentAt ? new Date(campaignDetail.sentAt).toLocaleString() : ""}</p></div>
                <button onClick={() => deleteCampaign(selectedCampaign)} className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" />Delete</button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Stat label="Sent" value={campaignDetail.sent||0} color="#1a73e8" />
                <Stat label="Opened" value={campaignDetail.uniqueOpenCount||0} sub={`${campaignDetail.openRate||0}%`} color="#34a853" />
                <Stat label="Clicked" value={campaignDetail.uniqueClickCount||0} sub={`${campaignDetail.clickRate||0}%`} color="#fbbc04" />
                <Stat label="Survey" value={campaignDetail.surveyCompleted||0} sub={`${campaignDetail.surveyRate||0}%`} color="#ea4335" />
                <Stat label="Re-opens" value={campaignDetail.opens||0} />
              </div>

              {/* Funnel */}
              <div className="bg-white rounded-xl border border-[#e8eaed] p-5">
                <h3 className="text-[13px] font-medium text-[#202124] mb-4">Funnel</h3>
                {[
                  { l: "Sent", v: campaignDetail.sent||0, p: 100, c: "#1a73e8" },
                  { l: "Opened", v: campaignDetail.uniqueOpenCount||0, p: campaignDetail.sent ? (campaignDetail.uniqueOpenCount||0)/campaignDetail.sent*100 : 0, c: "#34a853" },
                  { l: "Clicked", v: campaignDetail.uniqueClickCount||0, p: campaignDetail.sent ? (campaignDetail.uniqueClickCount||0)/campaignDetail.sent*100 : 0, c: "#fbbc04" },
                  { l: "Survey", v: campaignDetail.surveyCompleted||0, p: campaignDetail.sent ? (campaignDetail.surveyCompleted||0)/campaignDetail.sent*100 : 0, c: "#ea4335" },
                ].map(s => (
                  <div key={s.l} className="flex items-center gap-3 mb-2.5">
                    <span className="text-[12px] text-[#5f6368] w-[80px] shrink-0">{s.l}</span>
                    <div className="flex-1 bg-[#f1f3f4] rounded-full h-6 overflow-hidden relative">
                      <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${Math.max(s.p, 1.5)}%`, backgroundColor: s.c }} />
                      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-medium" style={{ color: s.p > 35 ? "#fff" : "#3c4043" }}>{s.v} ({s.p.toFixed(1)}%)</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recipients */}
              <div className="bg-white rounded-xl border border-[#e8eaed] overflow-hidden">
                <div className="px-5 py-3 border-b border-[#e8eaed]"><h3 className="text-[13px] font-medium text-[#202124]">Recipients ({(campaignDetail.recipientList||[]).length})</h3></div>
                <div className="max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-[13px]">
                    <thead className="sticky top-0 bg-[#f8f9fa]"><tr className="text-left border-b border-[#e8eaed]">
                      <Th>Email</Th><Th style={{textAlign:"center"}}>Opened</Th><Th style={{textAlign:"center"}}>Clicks</Th><Th style={{textAlign:"center"}}>Survey</Th><Th>Last Activity</Th>
                    </tr></thead>
                    <tbody>
                      {(campaignDetail.recipientList||[]).map((r,i) => (
                        <tr key={i} className="border-b border-[#f1f3f4] hover:bg-[#f8f9fa]">
                          <Td>{r.email}</Td>
                          <td className="py-2.5 text-center">{r.opened ? <Eye className="w-4 h-4 text-[#34a853] mx-auto" /> : <span className="text-[#dadce0]">—</span>}</td>
                          <td className="py-2.5 text-center">{r.clicked ? <span className="text-[12px] font-medium text-[#1a73e8]">{r.clickCount||1}</span> : <span className="text-[#dadce0]">—</span>}</td>
                          <td className="py-2.5 text-center">{r.surveyCompleted ? <CheckCircle2 className="w-4 h-4 text-[#34a853] mx-auto" /> : <span className="text-[#dadce0]">—</span>}</td>
                          <Td muted>{r.lastClickedAt ? new Date(r.lastClickedAt).toLocaleString() : r.lastOpenedAt ? new Date(r.lastOpenedAt).toLocaleString() : "—"}</Td>
                        </tr>
                      ))}
                      {!(campaignDetail.recipientList||[]).length && <tr><td colSpan="5" className="py-8 text-center text-[#80868b] text-[13px]">No recipients tracked yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : isLoading && !campaigns.length ? <SkeletonCards n={3} /> : (
            <div className="space-y-3">
              {campaigns.map(c => (
                <div key={c.id} className="bg-white rounded-xl border border-[#e8eaed] p-4 hover:shadow-sm transition-all group">
                  <div className="flex items-center justify-between mb-2">
                    <button onClick={() => loadCampaign(c.id)} className="text-left flex-1" data-testid={`campaign-${c.id}`}>
                      <p className="text-[14px] font-medium text-[#202124] group-hover:text-[#1a73e8] transition-colors">{c.campaignName || "Untitled"}</p>
                      <p className="text-[11px] text-[#80868b] mt-0.5">{c.sentAt ? new Date(c.sentAt).toLocaleString() : ""}</p>
                    </button>
                    <button onClick={() => deleteCampaign(c.id)} className="p-1.5 text-[#dadce0] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-2 shrink-0" title="Delete campaign"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <button onClick={() => loadCampaign(c.id)} className="w-full text-left" data-testid={`campaign-stats-${c.id}`}>
                    <div className="grid grid-cols-4 gap-3">
                      <div><p className="text-[10px] text-[#9aa0a6] uppercase">Sent</p><p className="text-[16px] font-light text-[#202124]">{c.sent}</p></div>
                      <div><p className="text-[10px] text-[#9aa0a6] uppercase">Opened</p><p className="text-[16px] font-light text-[#34a853]">{c.uniqueOpenCount}<span className="text-[11px] text-[#80868b] ml-1">{c.openRate}%</span></p></div>
                      <div><p className="text-[10px] text-[#9aa0a6] uppercase">Clicked</p><p className="text-[16px] font-light text-[#1a73e8]">{c.uniqueClickCount}<span className="text-[11px] text-[#80868b] ml-1">{c.clickRate}%</span></p></div>
                      <div><p className="text-[10px] text-[#9aa0a6] uppercase">Opens</p><p className="text-[16px] font-light text-[#5f6368]">{c.opens}</p></div>
                    </div>
                  </button>
                </div>
              ))}
              {!campaigns.length && <Empty icon={Megaphone} text="No campaigns yet" sub="Send an email from Newsletter to start tracking." />}
            </div>
          )
        )}

        </div>
      </main>
    </div>
  );
};

/* ── Shared small components ── */
const Empty = ({ text, sub, icon: Icon }) => (
  <div className="text-center py-16 text-[#80868b]">
    {Icon && <Icon className="w-8 h-8 mx-auto mb-2 text-[#dadce0]" />}
    <p className="text-[14px]">{text}</p>
    {sub && <p className="text-[12px] mt-1">{sub}</p>}
  </div>
);

const ResponseRow = ({ r, onDel }) => {
  const [open, setOpen] = useState(false);
  const g = r.geo || {};
  const loc = g.city !== "unknown" ? `${g.city}, ${g.country}` : g.country || "—";
  return (
    <div className="bg-white rounded-xl border border-[#e8eaed] hover:shadow-sm transition-shadow">
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center justify-between text-left">
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-[#202124] truncate">{r.firstName} {r.lastName}</p>
          <p className="text-[11px] text-[#80868b]">{r.email} &middot; {r.companyName || "—"} &middot; {new Date(r.submittedAt).toLocaleDateString()}</p>
          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#9aa0a6]">
            <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{loc}</span>
            <span className="flex items-center gap-0.5"><Globe className="w-3 h-3" />{g.ip || "—"}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 ml-2">
          <button onClick={e => { e.stopPropagation(); onDel(r.id); }} className="p-1 text-[#dadce0] hover:text-red-500 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
          {open ? <ChevronUp className="w-3.5 h-3.5 text-[#9aa0a6]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#9aa0a6]" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[#f1f3f4] pt-3 space-y-1.5 text-[12px]">
          <div className="bg-[#f8f9fa] rounded-lg p-2.5 grid grid-cols-3 gap-1.5 text-[11px] mb-2">
            <div><span className="text-[#9aa0a6]">IP:</span> <span className="text-[#3c4043]">{g.ip||"—"}</span></div>
            <div><span className="text-[#9aa0a6]">Country:</span> <span className="text-[#3c4043]">{g.country||"—"}</span></div>
            <div><span className="text-[#9aa0a6]">City:</span> <span className="text-[#3c4043]">{g.city||"—"}</span></div>
            <div><span className="text-[#9aa0a6]">Region:</span> <span className="text-[#3c4043]">{g.region||"—"}</span></div>
            <div><span className="text-[#9aa0a6]">TZ:</span> <span className="text-[#3c4043]">{g.timezone||"—"}</span></div>
            <div><span className="text-[#9aa0a6]">ISP:</span> <span className="text-[#3c4043]">{g.asOrganization||"—"}</span></div>
          </div>
          {Object.entries(r).filter(([k]) => !["id","submittedAt","geo","sessionId"].includes(k)).map(([k, v]) => (
            <div key={k} className="flex gap-2"><span className="text-[#9aa0a6] min-w-[140px]">{k}</span><span className="text-[#3c4043]">{Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};

const PartialRow = ({ p, onDel }) => {
  const g = p.geo || {};
  const loc = g.city !== "unknown" ? `${g.city}, ${g.country}` : g.country || "—";
  const steps = ["Intro", "Company", "Stack", "Pain", "Insights", "Interest"];
  return (
    <div className="bg-white rounded-xl border border-[#e8eaed] px-4 py-3 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <p className="text-[13px] font-medium text-[#202124]">{p.firstName || ""}{p.lastName ? ` ${p.lastName}` : ""}{!p.firstName && !p.lastName && <span className="text-[#9aa0a6] italic">Anonymous</span>}</p>
          <p className="text-[11px] text-[#80868b]">{p.email || "—"} &middot; Dropped <span className="text-[#ea4335] font-medium">Step {(p.lastStep||0)+1} ({steps[p.lastStep]||"?"})</span> &middot; {new Date(p.lastUpdated).toLocaleDateString()}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5">{steps.map((_,i) => <div key={i} className={`w-5 h-1 rounded-full ${i <= (p.lastStep||0) ? "bg-[#fbbc04]" : "bg-[#e8eaed]"}`} />)}</div>
          <button onClick={() => onDel(p.id)} className="p-1 text-[#dadce0] hover:text-red-500 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="flex items-center gap-2 text-[10px] text-[#9aa0a6]">
        <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{loc}</span>
        <span className="flex items-center gap-0.5"><Globe className="w-3 h-3" />{g.ip||"—"}</span>
      </div>
    </div>
  );
};

/* ── Root ── */
const SurveyAdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem("survey_admin_token"));
  const [ok, setOk] = useState(null);

  useEffect(() => {
    if (!token) { setOk(false); return; }
    fetch(`${API}/api/auth/session`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.authenticated) setOk(true); else { localStorage.removeItem("survey_admin_token"); setToken(null); setOk(false); } })
      .catch(() => setOk(false));
  }, [token]);

  if (ok === null) return <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#1a73e8] border-t-transparent rounded-full animate-spin" /></div>;
  if (!ok) return <AuthScreen onAuth={t => { setToken(t); setOk(true); }} />;
  return <Dashboard token={token} onLogout={() => { localStorage.removeItem("survey_admin_token"); setToken(null); setOk(false); }} />;
};

export default SurveyAdminPage;
